'use client';

import React, { useState } from 'react';
import HeroSection from './HeroSection';
import SolutionsGrid from './SolutionsGrid';
import ROICalculator from './ROICalculator';
import ThreatModeling from './ThreatModeling';
import TechnicalSpecs from './TechnicalSpecs';

const CybersecurityInteractive = () => {
  const [isROIOpen, setIsROIOpen] = useState(false);
  const [isThreatModelOpen, setIsThreatModelOpen] = useState(false);

  const solutions = [
  {
    id: 'threat-detection',
    icon: 'ShieldExclamationIcon',
    title: 'Advanced Threat Detection',
    description: 'AI-powered threat intelligence and real-time monitoring to identify and neutralize threats before they impact your business',
    features: [
    'Machine learning-based anomaly detection',
    'Real-time threat intelligence feeds',
    'Automated incident response workflows',
    'Behavioral analytics and user monitoring']
  },
  {
    id: 'endpoint-security',
    icon: 'ComputerDesktopIcon',
    title: 'Endpoint Security',
    description: 'Endpoint security is the act of making sure that endpoints or section purposes of end-client gadgets are protected from malicious actors and threats are taken care of. Advanced Endpoint Security frameworks protect these endpoints on a network or in the cloud from cybersecurity dangers.',
    features: [
    'Next-gen antivirus protection',
    'Endpoint detection and response (EDR)',
    'Comprehensive protection from advanced malware',
    'Protection from evolving zero-day threats']
  },
  {
    id: 'network-security',
    icon: 'GlobeAltIcon',
    title: 'Network Security',
    description: 'Multi-layered network defense with next-generation firewalls, IDS/IPS, and secure access controls',
    features: [
    'Next-generation firewall (NGFW)',
    'Intrusion detection and prevention (IDS/IPS)',
    'Zero-trust network access (ZTNA)',
    'Network segmentation and micro-segmentation']
  },
  {
    id: 'data-protection',
    icon: 'LockClosedIcon',
    title: 'Data Loss Prevention',
    description: 'Protect sensitive data across all channels with advanced DLP policies and encryption technologies',
    features: [
    'Content-aware data classification',
    'Encryption at rest and in transit',
    'Data loss prevention (DLP) policies',
    'Cloud access security broker (CASB)']
  },
  {
    id: 'identity-access',
    icon: 'FingerPrintIcon',
    title: 'Identity & Access Management',
    description: 'Identity and access management is a critical part of any enterprise security plan, as it is inextricably linked to the security and productivity of organizations. A robust IAM system adds an important layer of protection by ensuring a consistent application of user access rules and policies.',
    features: [
    'Multi-factor authentication (MFA)',
    'Single sign-on (SSO) integration',
    'Privileged access management (PAM)',
    'Identity governance and administration']
  },
  {
    id: 'security-operations',
    icon: 'CommandLineIcon',
    title: 'Security Operations Center',
    description: '24/7 monitoring and incident response from our expert security operations team',
    features: [
    '24/7/365 security monitoring',
    'Threat hunting and investigation',
    'Incident response and forensics',
    'Security information and event management (SIEM)']
  },
  {
    id: 'multifactor-authentication',
    icon: 'KeyIcon',
    title: 'Multifactor Authentication',
    description: 'Multi-Factor Authentication (MFA) is a security system that verifies a user\'s identity by requiring multiple credentials. It is a critical component of identity and access management (IAM) that asks for more than just a username and password.',
    features: [
    'Code from smartphone verification',
    'Security question authentication',
    'Biometric verification (fingerprint, facial)',
    'Enhanced security layer beyond passwords']
  },
  {
    id: 'soar-solutions',
    icon: 'CogIcon',
    title: 'SOAR Solutions',
    description: 'Security orchestration involves interweaving people, processes, and technology in the most effective manner to strengthen security posture. SOAR technology automates up to 95% of all response actions requiring human review.',
    features: [
    'Automated response workflows',
    '95% automation of response actions',
    'Focus on critical security tasks',
    'Orchestrated security operations']
  },
  {
    id: 'next-gen-firewalls',
    icon: 'FireIcon',
    title: 'Next Gen Firewalls',
    description: 'Next Generation Firewall (NGFW) adds protective layers such as application-level inspection and advanced intrusion protection system with intelligence to traditional firewall boundaries along with standard port/protocol protection.',
    features: [
    'Deep packet inspection',
    'Application-level filtering',
    'Advanced intrusion prevention',
    'Protection from web-based malware and targeted attacks']
  },
  {
    id: 'network-access-control',
    icon: 'LockOpenIcon',
    title: 'Network Access Control',
    description: 'Network Access Control (NAC) technology is a key driver to secure your Enterprise wired and wireless networks from unauthorized access. Provides dynamic network access based on user profiles and privileges.',
    features: [
    'Dynamic network access control',
    'User profile-based privileges',
    'Authentication integration',
    'Auto-remediation and VLAN assignment']
  },
  {
    id: 'cloud-dc-security',
    icon: 'CloudIcon',
    title: 'Cloud & DC Security',
    description: 'Cloud & Datacenter Security refers to a broad set of rules, technologies, applications, and controls used to protect virtualized IP, data, applications, services, and associated IT infrastructure in the cloud and datacenter.',
    features: [
    'Virtualized infrastructure protection',
    'Data and application security',
    'Multi-cloud security monitoring',
    'Comprehensive datacenter controls']
  },
  {
    id: 'mobile-email-web-security',
    icon: 'EnvelopeIcon',
    title: 'Mobile, Email & Web Security',
    description: 'Comprehensive security for email, mobile, and web communications. Email messages are encrypted to protect against interception and unauthorized access to confidential information transmitted over the public internet.',
    features: [
    'Email encryption (data in transit)',
    'Mobile device security',
    'Web communication protection',
    'Prevents data interception']
  }];



  const technicalSpecs = [
  {
    category: 'Threat Detection & Prevention',
    features: [
    {
      name: 'AI-Powered Threat Intelligence',
      description: 'Machine learning algorithms analyze billions of threat indicators daily',
      included: true
    },
    {
      name: 'Real-Time Behavioral Analytics',
      description: 'User and entity behavior analytics (UEBA) for anomaly detection',
      included: true
    },
    {
      name: 'Advanced Malware Protection',
      description: 'Sandboxing and detonation chambers for zero-day threat analysis',
      included: true
    },
    {
      name: 'Automated Threat Response',
      description: 'Orchestrated response workflows with customizable playbooks',
      included: true
    }]

  },
  {
    category: 'Network Security',
    features: [
    {
      name: 'Next-Generation Firewall',
      description: 'Deep packet inspection with application-level filtering',
      included: true
    },
    {
      name: 'Intrusion Prevention System',
      description: 'Signature and anomaly-based IPS with automatic updates',
      included: true
    },
    {
      name: 'Zero-Trust Network Access',
      description: 'Identity-based access controls with continuous verification',
      included: true
    },
    {
      name: 'Network Segmentation',
      description: 'Micro-segmentation for lateral movement prevention',
      included: true
    }]

  },
  {
    category: 'Data Protection',
    features: [
    {
      name: 'End-to-End Encryption',
      description: 'AES-256 encryption for data at rest and in transit',
      included: true
    },
    {
      name: 'Data Loss Prevention',
      description: 'Content-aware DLP with policy-based controls',
      included: true
    },
    {
      name: 'Cloud Security Posture Management',
      description: 'Multi-cloud security monitoring and compliance',
      included: true
    },
    {
      name: 'Secure Backup & Recovery',
      description: 'Immutable backups with rapid recovery capabilities',
      included: true
    }]

  }];


  const handleAssessmentClick = () => {
    console.log('Opening security assessment form');
  };

  const handleLearnMore = (solutionId: string) => {
    console.log('Learning more about solution:', solutionId);
  };

  return (
    <>
      <HeroSection data-component-id="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx:238:6" data-component-path="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx" data-component-line="238" data-component-file="CybersecurityInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D"
        onAssessmentClick={() => setIsROIOpen(true)} />

      
      <SolutionsGrid data-component-id="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx:242:6" data-component-path="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx" data-component-line="242" data-component-file="CybersecurityInteractive.tsx" data-component-name="SolutionsGrid" data-component-content="%7B%22elementName%22%3A%22SolutionsGrid%22%7D"
        solutions={solutions}
        onLearnMore={handleLearnMore} />

      
      <TechnicalSpecs data-component-id="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx:247:6" data-component-path="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx" data-component-line="247" data-component-file="CybersecurityInteractive.tsx" data-component-name="TechnicalSpecs" data-component-content="%7B%22elementName%22%3A%22TechnicalSpecs%22%7D" specs={technicalSpecs} />

      
      <ROICalculator data-component-id="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx:250:6" data-component-path="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx" data-component-line="250" data-component-file="CybersecurityInteractive.tsx" data-component-name="ROICalculator" data-component-content="%7B%22elementName%22%3A%22ROICalculator%22%7D"
        isOpen={isROIOpen}
        onClose={() => setIsROIOpen(false)} />

      
      <ThreatModeling data-component-id="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx:255:6" data-component-path="src/app/cybersecurity-solutions/components/CybersecurityInteractive.tsx" data-component-line="255" data-component-file="CybersecurityInteractive.tsx" data-component-name="ThreatModeling" data-component-content="%7B%22elementName%22%3A%22ThreatModeling%22%7D"
        isOpen={isThreatModelOpen}
        onClose={() => setIsThreatModelOpen(false)} />

    </>);

};

export default CybersecurityInteractive;